/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import {ActionType, createAction} from 'typesafe-actions';
import {
	AccountRecoveryPasswordStrengthEvaluationRequestDTO,
	AccountRecoveryStateDTO,
	EnterPasswordForAccountRecoveryDTO,
	EnterPhoneVerificationCodeForAccountRecoveryDTO,
	PasswordStrengthDTO
} from '../../generated/iam-client';
import {ConstraintViolationDTO} from '../../model/validation';

export const accountRecoverySceneActivated =
	createAction('ACCOUNT_RECOVERY_SCENE_ACTIVATED')();

export const accountRecoverySceneDeactivated =
	createAction('ACCOUNT_RECOVERY_SCENE_DEACTIVATED')();

export const accountRecoverySceneStateRetrieved =
	createAction('ACCOUNT_RECOVERY_SCENE_STATE_RETRIEVED')<{ state: AccountRecoveryStateDTO }>();

export const accountRecoverySceneRetrieveStateNotFound =
	createAction('ACCOUNT_RECOVERY_SCENE_RETRIEVE_STATE_NOT_FOUND')();

export const accountRecoverySceneRetrieveStateUnexpectedError =
	createAction('ACCOUNT_RECOVERY_SCENE_RETRIEVE_STATE_UNEXPECTED_ERROR')();

export const accountRecoverySceneRequestRetryPhoneVerification =
	createAction('ACCOUNT_RECOVERY_SCENE_REQUEST_RETRY_PHONE_VERIFICATION')();

export const accountRecoverySceneRetryPhoneVerificationRequested =
	createAction('ACCOUNT_RECOVERY_SCENE_RETRY_PHONE_VERIFICATION_REQUESTED')();

export const accountRecoverySceneRetryPhoneVerificationNotFound =
	createAction('ACCOUNT_RECOVERY_SCENE_RETRY_PHONE_VERIFICATION_NOT_FOUND')();

export const accountRecoverySceneRetryPhoneVerificationUnexpectedError =
	createAction('ACCOUNT_RECOVERY_SCENE_RETRY_PHONE_VERIFICATION_UNEXPECTED_ERROR')();

export const accountRecoverySceneRequestRestartPhoneVerification =
	createAction('ACCOUNT_RECOVERY_SCENE_REQUEST_RESTART_PHONE_VERIFICATION')();

export const accountRecoverySceneRestartPhoneVerificationRequested =
	createAction('ACCOUNT_RECOVERY_SCENE_RESTART_PHONE_VERIFICATION_REQUESTED')();

export const accountRecoverySceneRestartPhoneVerificationNotFound =
	createAction('ACCOUNT_RECOVERY_SCENE_RESTART_PHONE_VERIFICATION_NOT_FOUND')();

export const accountRecoverySceneRestartPhoneVerificationUnexpectedError =
	createAction('ACCOUNT_RECOVERY_SCENE_RESTART_PHONE_VERIFICATION_UNEXPECTED_ERROR')();

export const accountRecoveryScenePhoneVerificationCodeInputChanged =
	createAction('ACCOUNT_RECOVERY_SCENE_PHONE_VERIFICATION_CODE_INPUT_CHANGED')();

export const accountRecoveryScenePhoneVerificationCodeSubmitted =
	createAction('ACCOUNT_RECOVERY_SCENE_PHONE_VERIFICATION_CODE_SUBMITTED')<{
		command: EnterPhoneVerificationCodeForAccountRecoveryDTO
	}>();

export const accountRecoveryScenePhoneVerificationCodeEntered =
	createAction('ACCOUNT_RECOVERY_SCENE_PHONE_VERIFICATION_CODE_ENTERED')();

export const accountRecoverySceneEnterPhoneVerificationCodeNotFound =
	createAction('ACCOUNT_RECOVERY_SCENE_ENTER_PHONE_VERIFICATION_CODE_NOT_FOUND')();

export const accountRecoverySceneEnterPhoneVerificationCodeValidationException =
	createAction('ACCOUNT_RECOVERY_SCENE_ENTER_PHONE_VERIFICATION_CODE_VALIDATION_EXCEPTION')<{
		violations: { [key: string]: ConstraintViolationDTO[] }
	}>();

export const accountRecoverySceneEnterPhoneVerificationCodeUnexpectedError =
	createAction('ACCOUNT_RECOVERY_SCENE_ENTER_PHONE_VERIFICATION_CODE_UNEXPECTED_ERROR')();

export const accountRecoveryScenePasswordInputChanged =
	createAction('ACCOUNT_RECOVERY_SCENE_PASSWORD_INPUT_CHANGED')<{
		evaluationRequest: AccountRecoveryPasswordStrengthEvaluationRequestDTO
	}>();

export const accountRecoveryScenePasswordStrengthEvaluated =
	createAction('ACCOUNT_RECOVERY_SCENE_PASSWORD_STRENGTH_EVALUATED')<{ passwordStrength: PasswordStrengthDTO }>();

export const accountRecoverySceneEvaluatePasswordStrengthNotFound =
	createAction('ACCOUNT_RECOVERY_SCENE_EVALUATE_PASSWORD_STRENGTH_NOT_FOUND')();

export const accountRecoverySceneEvaluatePasswordStrengthUnexpectedError =
	createAction('ACCOUNT_RECOVERY_SCENE_EVALUATE_PASSWORD_STRENGTH_UNEXPECTED_ERROR')();

export const accountRecoveryScenePasswordSubmitted =
	createAction('ACCOUNT_RECOVERY_SCENE_PASSWORD_SUBMITTED')<{ command: EnterPasswordForAccountRecoveryDTO }>();

export const accountRecoveryScenePasswordEntered =
	createAction('ACCOUNT_RECOVERY_SCENE_PASSWORD_ENTERED')();

export const accountRecoverySceneEnterPasswordNotFound =
	createAction('ACCOUNT_RECOVERY_SCENE_ENTER_PASSWORD_NOT_FOUND')();

export const accountRecoverySceneEnterPasswordValidationException =
	createAction('ACCOUNT_RECOVERY_SCENE_ENTER_PASSWORD_VALIDATION_EXCEPTION')<{
		violations: { [key: string]: ConstraintViolationDTO[] }
	}>();

export const accountRecoverySceneEnterPasswordUnexpectedError =
	createAction('ACCOUNT_RECOVERY_SCENE_ENTER_PASSWORD_UNEXPECTED_ERROR')();

export const accountRecoverySceneStateChanged =
	createAction('ACCOUNT_RECOVERY_SCENE_STATE_CHANGED')<{ state: AccountRecoveryStateDTO }>();

export const accountRecoverySceneBackButtonClicked =
	createAction('ACCOUNT_RECOVERY_SCENE_BACK_BUTTON_CLICKED')();

// noinspection DuplicatedCode
export type ActionTypes =
	ActionType<typeof accountRecoverySceneActivated> |
	ActionType<typeof accountRecoverySceneDeactivated> |
	ActionType<typeof accountRecoverySceneStateRetrieved> |
	ActionType<typeof accountRecoverySceneRetrieveStateNotFound> |
	ActionType<typeof accountRecoverySceneRetrieveStateUnexpectedError> |
	ActionType<typeof accountRecoverySceneRequestRetryPhoneVerification> |
	ActionType<typeof accountRecoverySceneRetryPhoneVerificationRequested> |
	ActionType<typeof accountRecoverySceneRetryPhoneVerificationNotFound> |
	ActionType<typeof accountRecoverySceneRetryPhoneVerificationUnexpectedError> |
	ActionType<typeof accountRecoverySceneRequestRestartPhoneVerification> |
	ActionType<typeof accountRecoverySceneRestartPhoneVerificationRequested> |
	ActionType<typeof accountRecoverySceneRestartPhoneVerificationNotFound> |
	ActionType<typeof accountRecoverySceneRestartPhoneVerificationUnexpectedError> |
	ActionType<typeof accountRecoveryScenePhoneVerificationCodeInputChanged> |
	ActionType<typeof accountRecoveryScenePhoneVerificationCodeSubmitted> |
	ActionType<typeof accountRecoveryScenePhoneVerificationCodeEntered> |
	ActionType<typeof accountRecoverySceneEnterPhoneVerificationCodeNotFound> |
	ActionType<typeof accountRecoverySceneEnterPhoneVerificationCodeValidationException> |
	ActionType<typeof accountRecoverySceneEnterPhoneVerificationCodeUnexpectedError> |
	ActionType<typeof accountRecoveryScenePasswordInputChanged> |
	ActionType<typeof accountRecoveryScenePasswordStrengthEvaluated> |
	ActionType<typeof accountRecoverySceneEvaluatePasswordStrengthNotFound> |
	ActionType<typeof accountRecoverySceneEvaluatePasswordStrengthUnexpectedError> |
	ActionType<typeof accountRecoveryScenePasswordSubmitted> |
	ActionType<typeof accountRecoveryScenePasswordEntered> |
	ActionType<typeof accountRecoverySceneEnterPasswordNotFound> |
	ActionType<typeof accountRecoverySceneEnterPasswordValidationException> |
	ActionType<typeof accountRecoverySceneEnterPasswordUnexpectedError> |
	ActionType<typeof accountRecoverySceneStateChanged> |
	ActionType<typeof accountRecoverySceneBackButtonClicked>;
